import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack } from '@mui/material';

import ComputerFrame from '@/designs-dev/device/ComputerFrame';
import ScaleToFit from '@/designs-dev/device/ScaleToFit';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import {
  computerScreenHeight,
  computerScreenWidth,
  type Viewport,
} from '@/designs-dev/device/viewport';
import Descoping from '@/cam-admin/descoping/Descoping';
import { descopingScenes } from '@/cam-admin/descoping/Descoping.scenes';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import StateSelector from '@/designs-dev/inspect/StateSelector';

// Descoping CAM on a centred stage inside the brand laptop frame, matching the
// other CAM stages. The chrome floating at the foot of the stage is
// prototype-only: drill mode toggle, then state select, with the inspect panel
// docked beside the stage.
const Stage = () => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sceneId, setSceneId] = useState(
    () => searchParams.get('scene') ?? descopingScenes[0].id,
  );
  // Clamp to a scene that exists, so a stale ?scene never breaks the stage.
  const currentScene =
    descopingScenes.find((s) => s.id === sceneId) ?? descopingScenes[0];

  // Reflect the active scene in the URL so the view is a shareable address.
  useEffect(() => {
    if (searchParams.get('scene') !== currentScene.id) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', currentScene.id);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScene.id]);

  // Laptop mockup, full-bleed fill, or pinned to a breakpoint width.
  // A web design opens at the laptop screen's width with no laptop drawn round
  // it: same screen as the frame, judged at the size it renders. The frame is
  // still a click away in the toggle for showing the thing off.
  const [viewport, setViewport] = useState<Viewport>(computerScreenWidth);
  const framed = viewport === 'frame';
  // Screen element for drill mode when unframed (ComputerFrame provides its
  // own when framed).
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  // Esc is staged: a first press clears a selected layer, a second (nothing
  // selected) leaves drill mode. Matches the other stages.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        setActive(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, setActive, setCurrent, setHovered]);

  // Prototype-only control: the state switcher, in the bar at the stage's foot.
  // Switching state clears any pinned component, so a ?select doesn't linger
  // onto the new (remounted) scene's nodes.
  const pickScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setSceneId(id);
  };

  // key remounts the prototype in the chosen state.
  const page = (
    <Descoping
      key={currentScene.id}
      careSearch={currentScene.initial.careSearch}
      onNavigate={(path) => navigate(path)}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            // Room for the floating controls under the screen.
            pb: STAGE_BAR_ROOM,
            gap: 1.5,
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: framed ? 'center' : 'stretch',
              justifyContent: 'center',
            }}
          >
            {/* The Stack must span the full stage width — the laptop measures
                its wrapper to size itself, and an auto-width parent would make
                it measure (and feed back on) its own width. */}
            <Stack
              sx={{ alignItems: 'center', width: '100%', minHeight: 0 }}
              spacing={2}
            >
              {framed ? (
                <ComputerFrame>
                  {page}
                  <InspectOverlay />
                </ComputerFrame>
              ) : (
                /* Out of the frame. 'fill' takes the stage's own width and
                 height; a fixed width lays out at that width and is scaled
                 down to whatever room there is, so a 1440 design is judged at
                 1440 without running off the side of the stage. */
                <ScaleToFit
                  width={viewport === 'fill' ? 'fill' : viewport}
                  height={
                    viewport === 'fill' ? undefined : computerScreenHeight
                  }
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      borderRadius: `${radius.md}px`,
                      border: 1,
                      borderColor: 'custom.neutral.200',
                      bgcolor: 'background.default',
                    }}
                    ref={(el: HTMLElement | null) => setFullEl(el)}
                  >
                    <PhoneScreenContext.Provider value={fullEl}>
                      {page}
                      <InspectOverlay />
                    </PhoneScreenContext.Provider>
                  </Box>
                </ScaleToFit>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Two pills, side by side, floating at the foot of the stage: how
            you are looking at the design, then what it is showing. */}
        <StageBar>
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          <StateSelector
            scenes={descopingScenes}
            sceneId={currentScene.id}
            onSceneChange={pickScene}
          />
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const DescopingStage = () => (
  <InspectProvider>
    <Stage />
  </InspectProvider>
);

export default DescopingStage;
